import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm, ValidationMode } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { PreviewBlock } from '../components/Links/previewBlock'
import { Select } from '../components/Select'
import { TextArea } from '../components/TextArea'
import { Toggle } from '../components/Toggle'
import { settingsSchema } from '../helpers/validationSchema'
import { ModalWrapper } from '../modals/ModalWrapper'
import { getSettingsService, updateSettingsService } from '../services/setting'

export const Settings = () => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)

  const methods = useForm<SettingsFormFields>({
    resolver: yupResolver(settingsSchema),
    defaultValues: {
      metaTitle: '',
      metaDescription: '',
      showSensitive: false,
      sensitiveAge: '',
    },
    mode: 'all' as keyof ValidationMode,
  })

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = methods

  const onSubmit: SubmitHandler<SettingsFormFields> = async (data) => {
    // Not update if there is nothing changed
    if (!isDirty) {
      return
    }

    const settingsData = {
      meta_title: data.metaTitle,
      meta_description: data.metaDescription,
      show_sensitive: data.showSensitive,
      age_sensitive: data.sensitiveAge
    }
    const response = await updateSettingsService(settingsData)

    if (response.status === 200) {
      toast.success(response.data.message)
      setShowModal(true)
      reset(data)
    }
  }

  const fetchSettingsData = async () => {
    try {
      const settings = (await getSettingsService()) as Settings

      if (settings) {
        reset({
          metaTitle: settings.meta_title || '',
          metaDescription: settings.meta_description || '',
          showSensitive: settings.show_sensitive || false,
          sensitiveAge: settings.age_sensitive || '',
        })
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    fetchSettingsData()
  }, [])

  return (
    <>
      <div className="flex flex-col sm:flex-row">
        <div
          className="flex flex-col overflow-y-scroll p-6 sm:top-0 sm:ml-[100px] sm:w-[50%]"
          style={{ height: 'calc(100vh - 89px)' }}
        >
          <div className="mx-auto mb-8 w-full sm:max-w-[200px] md:mb-0 md:max-w-[350px]">
            <form
              className="mx-auto mb-9 flex max-w-[350px] flex-col"
              onSubmit={handleSubmit(onSubmit)}
            >
              <p className="mb-2 text-[18px] font-medium">
                {t('sensitiveMaterial')}
              </p>
              <div className="mb-3 flex gap-4 sm:gap-2 lg:gap-8 xl:gap-10">
                <p className="text-sm leading-none">
                  {t('displaySensitiveWarning')}
                </p>
                <Toggle
                  onToggle={(value) => setValue('showSensitive', value)}
                  enabled={watch().showSensitive || false}
                />
              </div>

              <div className="mb-4">
                <p className="mb-2.5 text-sm font-medium">{t('selectOne')}</p>
                <Select
                  variant="primary"
                  placeholder={t('sensitiveContent')}
                  options={['+18', '+21', '+25']}
                  className="text-sm"
                  {...register('sensitiveAge')}
                />
              </div>

              <p className="mb-2 text-[18px] font-medium">{t('seo')}</p>
              <p className="mb-2.5 text-sm leading-none">
                {t('changeMetadataMessage')}
              </p>
              <Input
                variant={errors.metaTitle ? 'danger' : 'third'}
                placeholder={t('metaTitle')}
                type="text"
                helperText={errors.metaTitle?.message as string}
                className="text-sm"
                {...register('metaTitle')}
              />
              <TextArea
                variant={errors.metaDescription ? 'danger' : 'third'}
                placeholder={t('metaDescription')}
                className="text-sm"
                helperText={errors.metaDescription?.message as string}
                {...register('metaDescription')}
              />

              <Button
                text={t('save')}
                type="submit"
                className="mt-5 w-full text-white"
              />
            </form>
          </div>
        </div>

        <div
          className="hidden w-[50%] items-center sm:flex"
          style={{ height: 'calc(100vh - 89px)' }}
        >
          <PreviewBlock />
        </div>
      </div>

      {/* Info message after Settings update */}
      <ModalWrapper onClose={() => setShowModal(false)} showModal={showModal}>
        <div className="flex flex-col px-4 pb-6 pt-10">
          <p className="mb-5 text-center text-2xl">
            {t('changeMetadataMessage')}
          </p>
        </div>
      </ModalWrapper>
    </>
  )
}
